import { getAgentUrl } from "src/util/url";
import {
  describeStreamFailure,
  endStreamTelemetry,
  noteStreamEvent,
  startStreamTelemetry,
} from "./streamTelemetry";

const EVENT_TYPE = {
  ON_MESSAGE_CHUNK: "message_chunk",
  ON_MESSAGE: "message",
  ON_CURRENT_TOOL_USE: "current_tool_use",
  GENERIC_EVENT: "event",
  STREAM_COMPLETE: "stream_complete",
  PIPELINE_STEP: "pipeline_step",
  PIPELINE_COMPLETE: "pipeline_complete",
  PIPELINE_CANCELLED: "pipeline_cancelled",
  DASHBOARD_PERSISTED: "dashboard_persisted",
  CONNECTION_ESTABLISHED: "connection_established",
  TEMPLATE_CHUNK: "template_chunk",
  ON_NEW_DASHBOARD: "new_dashboard",
};

/** Alias kept for older BE frames. */
const PIPELINE_COMPLETED_ALIAS = "pipeline_completed";

/**
 * Build type → handler map. Handlers may return `{ complete: true }` to end
 * the read loop. Unknown types are ignored (heartbeats, etc.).
 */
const buildEventHandlers = ({
  params,
  callbacks,
  runKey,
  getTelemetryChatId,
}) => {
  const {
    onMessageStart,
    onMessage,
    onMessageChunk,
    onCurrentToolUse,
    onConnectionEstablished,
    onPipelineStep,
    onPipelineComplete,
    onPipelineCancelled,
    onDashboardPersisted,
    onTemplateChunk,
    onNewDashboard,
    onDone,
  } = callbacks;

  // Idempotent: same-buffer message + stream_complete must not double-fire.
  let ended = false;
  const endOk = (reason, finalMessage) => {
    if (ended) {
      return { complete: true };
    }
    ended = true;
    endStreamTelemetry(runKey, getTelemetryChatId(), reason);
    onDone?.(finalMessage ?? null);
    return { complete: true };
  };

  return {
    [EVENT_TYPE.GENERIC_EVENT]: (data) => {
      if (data?.event?.messageStart?.role === "assistant") {
        onMessageStart?.(data);
      }
    },
    [EVENT_TYPE.CONNECTION_ESTABLISHED]: (data) => {
      onConnectionEstablished?.(data);
    },
    [EVENT_TYPE.ON_MESSAGE_CHUNK]: (data) => {
      onMessageChunk?.(data);
    },
    [EVENT_TYPE.ON_MESSAGE]: (data) => {
      const raw = data.final_message ?? data.message;
      const final_message = raw
        ? {
            ...raw,
            id: raw.id ?? `temp-assistant-${Date.now()}`,
            aiChatId: raw.aiChatId ?? params.aiChatId,
          }
        : null;
      onMessage?.({ ...data, final_message });

      if (final_message) {
        return endOk("message", final_message);
      }
      return undefined;
    },
    [EVENT_TYPE.ON_CURRENT_TOOL_USE]: (data) => {
      onCurrentToolUse?.(data);
    },
    [EVENT_TYPE.PIPELINE_STEP]: (data) => {
      onPipelineStep?.(data);
    },
    [EVENT_TYPE.PIPELINE_COMPLETE]: (data) => {
      onPipelineComplete?.(data);
    },
    [PIPELINE_COMPLETED_ALIAS]: (data) => {
      onPipelineComplete?.(data);
    },
    [EVENT_TYPE.PIPELINE_CANCELLED]: (data) => {
      onPipelineCancelled?.(data);
    },
    [EVENT_TYPE.DASHBOARD_PERSISTED]: (data) => {
      onDashboardPersisted?.(data);
    },
    [EVENT_TYPE.TEMPLATE_CHUNK]: (data) => {
      onTemplateChunk?.(data);
    },
    [EVENT_TYPE.ON_NEW_DASHBOARD]: (data) => {
      onNewDashboard?.(data);
    },
    [EVENT_TYPE.STREAM_COMPLETE]: (data) => {
      // Fallback for pipeline-only / v1 paths that never send
      // message+final_message (PR #5643 regressed this).
      return endOk("stream_complete", data?.final_message ?? null);
    },
  };
};

const streamAIResponse = async (
  params,
  {
    tenantId,
    apiUrl,
    onMessageStart,
    onMessage,
    onMessageChunk,
    onCurrentToolUse,
    onConnectionEstablished,
    onPipelineStep,
    onPipelineComplete,
    onPipelineCancelled,
    onDashboardPersisted,
    onTemplateChunk,
    onNewDashboard,
    onError,
    onDone,
    headers = {},
    agentName,
    signal,
  }
) => {
  const runKey = `run-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  let telemetryChatId = params?.aiChatId ?? null;
  startStreamTelemetry({
    chatId: telemetryChatId,
    agentName,
    runKey,
  });

  const handlers = buildEventHandlers({
    params,
    callbacks: {
      onMessageStart,
      onMessage,
      onMessageChunk,
      onCurrentToolUse,
      onConnectionEstablished,
      onPipelineStep,
      onPipelineComplete,
      onPipelineCancelled,
      onDashboardPersisted,
      onTemplateChunk,
      onNewDashboard,
      onDone,
    },
    runKey,
    getTelemetryChatId: () => telemetryChatId,
  });

  try {
    const response = await fetch(getAgentUrl({ tenantId, apiUrl, agentName }), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: headers?.Authorization,
        "x-workspace-id": headers?.["x-workspace-id"],
      },
      body: JSON.stringify({
        ...params,
        headers,
        stream: true,
      }),
      signal,
    });

    if (!response.ok) {
      const retryAfter = response.headers.get("Retry-After");
      // Read the body once as text — calling .json() then .text() throws
      // "body stream already read" for non-JSON error responses.
      const rawBody = await response.text();
      let errorBody;
      try {
        errorBody = JSON.parse(rawBody);
      } catch {
        errorBody = { message: rawBody };
      }
      endStreamTelemetry(
        runKey,
        telemetryChatId,
        "http_error",
        `HTTP ${response.status}`
      );
      onError?.(
        describeStreamFailure(
          {
            status: response.status,
            retryAfter: retryAfter ? parseInt(retryAfter, 10) : null,
            message:
              errorBody?.message ||
              `Request failed with status ${response.status}`,
            ...errorBody,
          },
          {
            chatId: telemetryChatId,
            runKey,
            endReason: "http_error",
          }
        )
      );
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let complete = false;

    while (!complete) {
      const { done, value } = await reader.read();

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        if (!line.startsWith("data: ")) {
          continue;
        }

        let data;
        try {
          data = JSON.parse(line.replace("data: ", ""));
        } catch {
          // Partial chunk boundary or non-JSON keepalive — skip this frame
          // instead of letting one bad line kill the whole stream.
          continue;
        }

        const eventType =
          data?.type ?? (data?.error ? "error-frame" : "unknown");
        noteStreamEvent(runKey, telemetryChatId, eventType, {
          toolName:
            eventType === EVENT_TYPE.ON_CURRENT_TOOL_USE
              ? data?.name || data?.tool_name || data?.display_name
              : undefined,
          adoptChatId:
            eventType === EVENT_TYPE.CONNECTION_ESTABLISHED
              ? data?.aiChatId
              : undefined,
          thinking:
            eventType === EVENT_TYPE.CONNECTION_ESTABLISHED &&
            typeof data?.thinking === "boolean"
              ? data.thinking
              : undefined,
          stepLabel:
            eventType === EVENT_TYPE.PIPELINE_STEP
              ? [data?.step, data?.status].filter(Boolean).join(" · ")
              : eventType === EVENT_TYPE.ON_CURRENT_TOOL_USE
                ? data?.display_name || data?.name || data?.tool_name
                : eventType === EVENT_TYPE.ON_MESSAGE
                  ? data?.final_message
                    ? "final"
                    : data?.status || undefined
                  : eventType === EVENT_TYPE.TEMPLATE_CHUNK
                    ? "template"
                    : undefined,
        });
        if (eventType === EVENT_TYPE.CONNECTION_ESTABLISHED && data?.aiChatId) {
          telemetryChatId = data.aiChatId;
        }

        // pipeline_step uses `error` for retry/fail detail — not stream-fatal.
        const isFatalStreamError =
          data?.type === "error" ||
          (Boolean(data?.error) && data?.type !== EVENT_TYPE.PIPELINE_STEP);

        if (isFatalStreamError) {
          endStreamTelemetry(
            runKey,
            telemetryChatId,
            "fatal",
            typeof data?.error === "string" ? data.error : data?.type
          );
          onError?.(
            describeStreamFailure(data, {
              chatId: telemetryChatId,
              runKey,
              endReason: "fatal",
            })
          );
          complete = true;
          // Do not also run type dispatch (e.g. pipeline_cancelled + error).
          break;
        }

        const result = handlers[data?.type]?.(data);
        if (result?.complete) {
          complete = true;
          // Stop same-buffer siblings (e.g. message then stream_complete).
          break;
        }
      }

      if (done && !complete) {
        endStreamTelemetry(
          runKey,
          telemetryChatId,
          "ended_early",
          "Stream ended early."
        );
        onError?.(
          describeStreamFailure("Stream ended early.", {
            chatId: telemetryChatId,
            runKey,
            endReason: "ended_early",
          })
        );
        complete = true;
      }
    }
  } catch (e) {
    if (signal?.aborted) {
      endStreamTelemetry(runKey, telemetryChatId, "aborted");
      return;
    }
    endStreamTelemetry(
      runKey,
      telemetryChatId,
      "exception",
      String(e?.message ?? e)
    );
    onError?.(
      describeStreamFailure(e, {
        chatId: telemetryChatId,
        runKey,
        endReason: "exception",
      })
    );
  }
};

export default streamAIResponse;
